import React, { FunctionComponent, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Grid, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { formatInsightsHubDisplayDate } from "@insightsHub/util/DateFormat";
import theme from "@app/constants/theme";
import { AppRoute } from "@app/models/AppRoute";
import { Button } from "@shared/components";
import { ContentButtonSize, InsightsMainContentProps } from "./models";
import ContentTypeChip from "./ContentTypeChip";

const useStyles = makeStyles(() => ({
  title: {
    letterSpacing: 0,

    [theme.breakpoints.up("xl")]: {
      fontSize: 60,
    },
    [theme.breakpoints.between("lg", "xl")]: {
      fontSize: 35,
    },
    [theme.breakpoints.between("md", "lg")]: {
      fontSize: 28,
    },
    [theme.breakpoints.between("sm", "md")]: {
      fontSize: 24,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: 43,
      letterSpacing: 0.63,
    },
  },
  carouselTextContainer: {
    background: theme.palette.common.white,
    flexWrap: "nowrap",
    height: 320,
    padding: theme.spacing(5, 11, 0, 11),
    [theme.breakpoints.up("xl")]: {
      height: 320,
      padding: theme.spacing(5, 11, 0, 11),
    },
    [theme.breakpoints.between("lg", "xl")]: {
      height: 320,
      padding: theme.spacing(5, 11, 0, 11),
    },
    [theme.breakpoints.between("md", "lg")]: {
      height: 320,
      padding: theme.spacing(0, 11, 0, 11),
    },
    [theme.breakpoints.between("sm", "md")]: {
      width: "auto",
      height: 270,
      padding: theme.spacing(0, 9),
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: 350,
      padding: theme.spacing(4, 8),
    },
    justifyContent: "flex-start",
    textAlign: "left",
  },
  slideTitle: {
    width: "100%",
    textAlign: "left",
    wordBreak: "break-word",
    webkitLineClamp: 2,
    lineClamp: 2,
    textOverflow: "ellipsis",
    overflow: "hidden",
    display: "-webkit-box",
    boxOrient: "vertical",
    paddingTop: theme.spacing(4),
    [theme.breakpoints.up("xl")]: {
      fontSize: 20,
      letterSpacing: 1,
      lineHeight: 1.2,
    },
    [theme.breakpoints.between("lg", "xl")]: {
      fontSize: 20,
      letterSpacing: 0.56,
      lineHeight: 1.2,
    },
    [theme.breakpoints.down("lg")]: {
      fontSize: 20,
      letterSpacing: 0,
      lineHeight: 1.2,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: 25,
      letterSpacing: 0.56,
    },
  },
  slideDate: {
    fontSize: 15,

    letterSpacing: 0.4,
    [theme.breakpoints.up("xl")]: {
      fontSize: 15,
      letterSpacing: 0.4,
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: 20,
      letterSpacing: 0.4,
    },
  },
  slideDescription: {
    width: "100%",
    textAlign: "left",
    webkitLineClamp: 4,
    lineClamp: 4,
    wordBreak: "break-word",
    textOverflow: "ellipsis",
    overflow: "hidden",
    display: "-webkit-box",
    boxOrient: "vertical",
    fontSize: 20,
    letterSpacing: 0.3,
    lineHeight: 1.3,
    maxHeight: 200,
    paddingTop: theme.spacing(4),
  },
  slideButton: {
    width: "100%",
    paddingTop: theme.spacing(5),
    fontSize: 20,
    letterSpacing: 0.4,
    [theme.breakpoints.down("sm")]: {
      fontSize: 25,
      letterSpacing: 0.5,
    },
  },
  mainImageContainer: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    [theme.breakpoints.up("xl")]: {
      position: "relative",
    },
    [theme.breakpoints.down("sm")]: {
      height: 230,
    },
  },
  authorContainer: {
    display: "grid",
    gridTemplateColumns: "17px auto",
    paddingTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "20px auto",
    },
  },
  slideAuthor: {
    margin: 0,
    textTransform: "uppercase",
    wordBreak: "break-word",
    fontWeight: 500,
    letterSpacing: 1.2,
    textAlign: "left",
    webkitLineClamp: 1,
    lineClamp: 1,
    textOverflow: "ellipsis",
    overflow: "hidden",
    display: "-webkit-box",
    boxOrient: "vertical",
    fontSize: 12,
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
      letterSpacing: 1.2,
    },
  },
  authorBy: {
    fontSize: 12,
    display: "-webkit-box",
    [theme.breakpoints.down("sm")]: {
      fontSize: 14,
      // letterSpacing: 0.56,
    },
  },
  mainImage: {
    [theme.breakpoints.up("xl")]: {
      width: 300,
      height: 542,
    },
    [theme.breakpoints.between("lg", "xl")]: {
      width: 300,
      height: 390,
    },
    [theme.breakpoints.between("md", "lg")]: {
      width: 300,
      height: 330,
    },
    [theme.breakpoints.between("sm", "md")]: {
      width: 491,
      height: 270,
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: 230,
    },
  },
}));
const InsightsHubSecondaryContent: FunctionComponent<InsightsMainContentProps> = ({
  title,
  url,
  description,
  id,
  contentType,
  authorName,
  publishDate,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const handleLearnMoreClick = useCallback((): void => {
    history.push({ pathname: `${AppRoute.InsightsHub}/${id}`, state: { from: "content-page" } });
  }, [history, id]);

  return (
    <Grid
      container
      sx={{
        flexDirection: {
          xs: "column",
          sm: "columc",
          backgroundColor: "white",
        },
      }}
      data-testid="feature-insights-section"
    >
      <Grid item sx={{ position: "relative" }}>
        <img src={url} width="100%" height={230} />
        {contentType && (
          <div style={{ position: "absolute", top: 0, left: 0, padding: 0, margin: 0, textTransform: "uppercase" }}>
            <ContentTypeChip label={contentType ? contentType : ""} chipType="content" />
          </div>
        )}
      </Grid>
      <Grid item>
        <Grid container direction="column" className={classes.carouselTextContainer} alignItems="center">
          <Grid item width="100%" className={classes.slideDate}>
            {formatInsightsHubDisplayDate(publishDate)}
          </Grid>
          <Grid item width="100%">
            <Typography title={title} variant="h5" className={classes.slideTitle}>
              {title}
            </Typography>
          </Grid>
          <Grid item width="100%">
            <div className={classes.authorContainer}>
              <div className={classes.authorBy}>BY</div>
              <div className={classes.slideAuthor}> {authorName}</div>
            </div>
          </Grid>
          <Grid item className={classes.slideDescription} title={description ?? undefined} width="100%">
            {description}
          </Grid>
          <Grid item className={classes.slideButton}>
            <Button
              shape="rounded"
              size={ContentButtonSize.Small}
              testId={`insight-learn-more-${id}`}
              onClick={handleLearnMoreClick}
            >
              READ MORE
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default InsightsHubSecondaryContent;
